import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IUser, IChangePassword, IBlockedUser, initialUserForm, IUserForm } from '../../../types/user/userInterfaces'

const userUpdatePasswordForm: IChangePassword = {
  id: 0,
  username: '',
  currentPassword: '',
  newPassword: '',
};

export interface UsersState {
  form: IUserForm;
  users: IUser[];
  blockedUsers: IBlockedUser[];
  paginator: any;
  paginatorBlockedUsers: any;
  userPasswordEdit: IChangePassword;
  pageSize: number;
  isCreateModalOpen: boolean;
  isUpdateModalOpen: boolean;
  isSubmitting: boolean;
  errors: Record<string, string>;
}

export const initialState: UsersState = {
  form: initialUserForm,
  users: [],
  blockedUsers: [],
  paginator: null,
  paginatorBlockedUsers: null,
  userPasswordEdit: userUpdatePasswordForm,
  pageSize: 5,
  isCreateModalOpen: false,
  isUpdateModalOpen: false,
  isSubmitting: false,
  errors: {},
};

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    loadingUsers: (state, action: PayloadAction<{ content: IUser[] }>) => {
      state.users = action.payload.content;
      state.paginator = action.payload;
    },
    loadingblockedUsers: (
      state,
      action: PayloadAction<{ content: IBlockedUser[] }>
    ) => {
      state.blockedUsers = action.payload.content;
      state.paginatorBlockedUsers = action.payload;
    },
    deactivateStateUser: (
      state,
      action: PayloadAction<{ id: number; status: boolean }>
    ) => {
      const { id, status: newState } = action.payload;
      state.users = state.users.map((user) =>
        user.id === id ? { ...user, status: newState } : user
      );
    },
    blockStateUser: (
      state,
      action: PayloadAction<{ id: number; locked: boolean }>
    ) => {
      const { id, locked: newState } = action.payload;
      state.blockedUsers = state.blockedUsers.map((user) =>
        user.id === id ? { ...user, locked: newState } : user
      );
    },
    onSelectedUserPassword: (state, action: PayloadAction<IChangePassword>) => {
      state.userPasswordEdit = action.payload;
    },
    setPageSize(state, action) {
      state.pageSize = action.payload;
    },
    updateUser: (state, action: PayloadAction<IUser>) => {
      state.users = state.users.map((user) =>
        user.id === action.payload.id ? { ...user, ...action.payload } : user
      );
    },

    setCreateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isCreateModalOpen = action.payload;
    },

    setUpdateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isUpdateModalOpen = action.payload;
    },

    /**
     * ✏️ Actualiza un solo campo del formulario de usuario.
     *
     * @param state - Estado actual del slice.
     * @param action - Objeto que contiene:
     *   - `field`: Nombre del campo del formulario
     *   - `value`: Valor nuevo
     *
     * ✅ Ideal para inputs controlados desde React
     * ✅ Compatible con formularios dinámicos
     */
    updateUserField(
      state,
      action: PayloadAction<{ field: keyof IUserForm; value: any }>
    ) {
      const { field, value } = action.payload;
      state.form = {
        ...state.form,
        [field]: value,
      };
    },

    /**
     * 🧹 Reinicia el formulario a su estado inicial.
     *
     * ✅ Ideal cuando se cierra el modal
     * ✅ También cuando se abre un modal limpio
     */
    resetUserForm(state) {
      state.form = { ...initialUserForm };
    },

    setSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },

    /**
     * ❌ Guarda los errores generados por el formulario
     * Se usa para mostrar mensajes de validación en los inputs.
     */
    setFormErrors(state, action: PayloadAction<Record<string, string>>) {
      state.errors = action.payload;
    },

    /**
     * ✅ Limpia todos los errores
     * Se ejecuta al cerrar modal o resetear formulario.
     */
    resetFormErrors(state) {
      state.errors = {};
    },

    /**
   * 🔁 Inserta o actualiza un usuario dentro de la tabla.
   *
   * ✅ Si existe, lo reemplaza
   * ✅ Si no existe, lo agrega al final
   */
    upsertUserInTable(state, action: PayloadAction<IUser>) {
      const user = action.payload;

      const index = state.users.findIndex((u) => u.id === user.id);

      if (index >= 0) {
        // ✅ Actualiza existente
        state.users[index] = { ...state.users[index], ...user };
      } else {
        // ✅ Inserta nuevo
        state.users.push(user);
      }
    },

    /**
   * 📝 Rellena el formulario con un usuario para edición
   */
    prefillUserForm(state, action) {
      const user = action.payload;

      state.form = {
        id: user.id,
        username: user.username,
        email: user.email,
        password: null,
        confirmPassword: null,
        roleIds: user.roles?.map((r: { id: number }) => r.id) ?? [],
        branchIds: user.branchIds ?? [],
        locked: user.locked,
        status: user.status,
        twoFactorEnabled: user.twoFactorEnabled,
      };
    }





  },
});

export const {
  loadingUsers,
  onSelectedUserPassword,
  deactivateStateUser,
  loadingblockedUsers,
  blockStateUser,
  setPageSize,
  updateUser,
  setCreateModalOpen,
  setUpdateModalOpen,

  updateUserField,
  resetUserForm,
  setSubmitting,

  setFormErrors,
  resetFormErrors,
  upsertUserInTable,
  prefillUserForm,
} = usersSlice.actions;

export default usersSlice.reducer;
